import {
  CheckmarkOutline,
  Error,
  Information,
  Warning,
} from '@carbon/react/icons';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import type { DcaWorkspaceWellRegressionTaskApi } from 'types/dca-workspace-wells-api/responses';
import { PlainLink } from 'components/link';
import styles from './well-jobs-sub-table.module.scss';

export type WellTaskTableRow = DcaWorkspaceWellRegressionTaskApi & {
  rowId: string;
  typeCurveDisplay: string;
  typeCurveHref: string | null;
};

/**
 * @param {number|string} jobId - Bulk job id, "manual" (saved by hand), or "legacy"
 *   (migrated from the legacy well_production_forecasts table — no workspace, no job)
 * @return {string} Display label for Job ID column
 */
export function jobLabel(jobId: number | string): string {
  if (jobId === 'manual') {
    return 'Manual';
  }
  if (jobId === 'legacy') {
    return 'Legacy';
  }
  return String(jobId);
}

/**
 * @param {DcaWorkspaceWellRegressionTaskApi} job - API job row
 * @param {number} index - Row index for disambiguation
 * @return {string} Stable id for selection + table row identity
 */
export function jobRowId(
  job: DcaWorkspaceWellRegressionTaskApi,
  index: number
): string {
  return `${job.dcaWellRegressionId}:${String(job.source)}:${String(
    job.jobId
  )}:${index}`;
}

const columnHelper = createColumnHelper<WellTaskTableRow>();

export const wellTasksColumns: ColumnDef<WellTaskTableRow, any>[] = [
  columnHelper.accessor((row) => jobLabel(row.jobId), {
    id: 'jobId',
    header: 'Job ID',
    size: 100,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('statusType', {
    id: 'status',
    header: 'Status',
    size: 120,
    meta: { type: 'text' },
    cell: (info) => {
      const status = info.getValue();
      const label = status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : status;
      let icon: React.ReactNode;
      switch (status) {
        case 'success':
          icon = <CheckmarkOutline className={styles.validIcon} />;
          break;
        case 'warning':
          icon = <Warning className={styles.invalidIcon} />;
          break;
        case 'error':
          icon = <Error className={styles.errorIcon} />;
          break;
        case 'notification':
          icon = <Information className={styles.notificationIcon} />;
          break;
        default:
          return <span>{label}</span>;
      }
      return (
        <div className={styles.statusCell}>
          {icon}
          <span>{label}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('message', {
    id: 'message',
    header: 'Message',
    size: 200,
    meta: { type: 'text' },
    cell: (info) => {
      const m = info.getValue();
      if (m === null || m === undefined || m === '') return '—';
      return (
        <span
          style={{
            display: 'inline-block',
            maxWidth: '18rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={m}
        >
          {m}
        </span>
      );
    },
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 220,
    meta: { type: 'date' },
  }),
  columnHelper.accessor('createdBy', {
    id: 'createdBy',
    header: 'Created By',
    size: 140,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('latest', {
    id: 'latest',
    header: 'Latest',
    size: 140,
    meta: {
      type: 'boolean',
      filterOptions: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
    },
    cell: (info) => (info.getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor('oilEurBbl', {
    id: 'oilEurBbl',
    header: 'Oil EUR (bbl)',
    size: 180,
    meta: { align: 'right', type: 'decimal', format: { maxDecimalPlaces: 0 } },
  }),
  columnHelper.accessor('gasEurMcf', {
    id: 'gasEurMcf',
    header: 'Gas EUR (mcf)',
    size: 180,
    meta: { align: 'right', type: 'decimal', format: { maxDecimalPlaces: 0 } },
  }),
  columnHelper.accessor('waterEurBbl', {
    id: 'waterEurBbl',
    header: 'Water EUR (bbl)',
    size: 180,
    meta: { align: 'right', type: 'decimal', format: { maxDecimalPlaces: 0 } },
  }),
  columnHelper.accessor('typeCurveDisplay', {
    id: 'typeCurve',
    header: 'Type Curve',
    size: 140,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '—';
      const href = info.row.original.typeCurveHref;
      if (!href) return value;
      return (
        <PlainLink href={href} target="_blank" rel="noopener noreferrer">
          {value}
        </PlainLink>
      );
    },
  }),
];
